import * as React from 'react'
import { createPortal } from 'react-dom'
import { HexColorPicker } from 'react-colorful'
import { useT } from '../../../app/lib/i18n'
import { getPortalContainer } from '../../lib/portalContainer'
import * as s from './ColorPicker.style.css'

function normalizeHex(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) return withHash.toUpperCase()
  if (/^#[0-9A-Fa-f]{3}$/.test(withHash)) {
    const [, a, b, c] = withHash
    return `#${a}${a}${b}${b}${c}${c}`.toUpperCase()
  }
  return null
}

export interface ColorPickerProps {
  value: string
  onChange: (hex: string) => void
  label?: string
  disabled?: boolean
  /** Show a hex text field next to the swatch. Default true. */
  showHexInput?: boolean
  id?: string
}

/**
 * Shared color picker: swatch trigger + optional hex input + saturation/hue popover.
 * Popover is portaled via getPortalContainer() so it is not clipped by
 * overflow:hidden ancestors (e.g. Modal) and still appears in browser fullscreen.
 */
export function ColorPicker({
  value,
  onChange,
  label,
  disabled = false,
  showHexInput = true,
  id,
}: ColorPickerProps) {
  const t = useT()
  const [open, setOpen] = React.useState(false)
  const [draftHex, setDraftHex] = React.useState(value)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const swatchRef = React.useRef<HTMLButtonElement>(null)
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const [popoverPos, setPopoverPos] = React.useState<{ top: number; left: number } | null>(null)
  const displayColor = normalizeHex(value) ?? '#6B7280'

  React.useEffect(() => {
    setDraftHex(value)
  }, [value])

  const updatePopoverPosition = React.useCallback(() => {
    const el = swatchRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setPopoverPos({
      top: rect.bottom + 8,
      left: rect.left,
    })
  }, [])

  React.useEffect(() => {
    if (!open) return
    updatePopoverPosition()
    const onScrollOrResize = () => updatePopoverPosition()
    window.addEventListener('resize', onScrollOrResize)
    window.addEventListener('scroll', onScrollOrResize, true)
    return () => {
      window.removeEventListener('resize', onScrollOrResize)
      window.removeEventListener('scroll', onScrollOrResize, true)
    }
  }, [open, updatePopoverPosition])

  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const commitHex = (raw: string) => {
    const next = normalizeHex(raw)
    if (next) {
      onChange(next)
      setDraftHex(next)
    } else {
      setDraftHex(displayColor)
    }
  }

  const popover =
    open && !disabled && popoverPos
      ? createPortal(
          <div
            ref={popoverRef}
            className={s.popover}
            role="dialog"
            aria-label={t('shared.colorPicker.chooseColor')}
            style={{
              position: 'fixed',
              top: popoverPos.top,
              left: popoverPos.left,
              marginTop: 0,
              zIndex: 11000,
            }}
          >
            <div className={s.picker}>
              <HexColorPicker
                color={displayColor}
                onChange={(hex) => {
                  const next = normalizeHex(hex) ?? hex.toUpperCase()
                  onChange(next)
                  setDraftHex(next)
                }}
              />
            </div>
          </div>,
          getPortalContainer(),
        )
      : null

  return (
    <div className={s.root} ref={rootRef}>
      {label ? (
        <label className={s.label} htmlFor={id}>
          {label}
        </label>
      ) : null}
      <div className={s.triggerRow}>
        <div className={s.popoverAnchor}>
          <button
            ref={swatchRef}
            type="button"
            id={id}
            className={s.swatchButton}
            style={{ backgroundColor: displayColor }}
            disabled={disabled}
            aria-label={label ? `${label} color picker` : 'Color picker'}
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => {
              if (!disabled) setOpen((v) => !v)
            }}
          />
        </div>
        {showHexInput ? (
          <input
            className={s.hexInput}
            value={draftHex}
            disabled={disabled}
            spellCheck={false}
            autoComplete="off"
            aria-label={label ? `${label} hex value` : 'Hex color'}
            onChange={(e) => setDraftHex(e.target.value)}
            onBlur={() => commitHex(draftHex)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitHex(draftHex)
              }
            }}
          />
        ) : null}
      </div>
      {popover}
    </div>
  )
}
